import { Handshake } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/features/auth';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import { usePartnerShareReport, usePartnerTotals } from '../api/reports.queries';
import { formatPeriodKey } from '../lib/report-labels';
import type { PartnerShareReport, PartnerShareRow, ReportParams } from '../types/report.types';
import { ReportNoData } from './report-no-data';

/** Where a country's partner is set up (Settlements › Partners). */
const PARTNERS_LINK = '/settlements?tab=partners';

const pct = (value: number | null) => (value === null ? '—' : `${value}%`);

/**
 * Partner share (reports.financial.read): per day or month and country, how
 * the partner's share is reached — gross profit, management fee, net profit,
 * the share % — what refunds paid in the period gave back, and the net share
 * we owe. Totals per country and currency come from the API.
 *
 * It is also the Partner's own report (reports.partner.read, the Partner
 * report page): without reports.financial.read the API leaves out gross profit,
 * management fee and net profit, and their columns are not shown.
 */
export function FinancialPartnerShare({
  params,
  filtered,
  onClearFilters,
}: {
  params: ReportParams;
  filtered: boolean;
  onClearFilters: () => void;
}) {
  const { can } = usePermissions();
  const report = usePartnerShareReport(params);

  if (report.isLoading) return <Skeleton className="h-64" />;
  if (report.error)
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;
  if (!report.data || report.data.rows.length === 0)
    return filtered ? (
      <ReportNoData filtered onClear={onClearFilters} />
    ) : (
      // The API lists only countries whose orders carried a partner share above 0%.
      <EmptyState
        icon={Handshake}
        title="No partner share in this period"
        description={
          can('reports.financial.read')
            ? "A partner share is taken only where the country's pricing parameter “Partner share %” is above 0. It is 0% in your countries (or there were no completed orders), so nothing is owed to a partner."
            : 'No share was earned in this period. Try a longer period.'
        }
      />
    );
  return <PartnerShareBody report={report.data} />;
}

/**
 * The Partner's totals (reports.partner.read), on its Financial summary and
 * above its Operational report: per country and currency, only what was
 * collected and its share after fees, which is all the API sends.
 */
export function PartnerShareTotals({
  params,
  filtered,
  onClearFilters,
}: {
  params: ReportParams;
  filtered: boolean;
  onClearFilters: () => void;
}) {
  const totals = usePartnerTotals(params);

  if (totals.isLoading) return <Skeleton className="h-32" />;
  if (totals.error)
    return <ErrorState error={totals.error} onRetry={() => void totals.refetch()} />;
  if (!totals.data || totals.data.length === 0)
    return filtered ? (
      <ReportNoData filtered onClear={onClearFilters} />
    ) : (
      <EmptyState
        icon={Handshake}
        title="No completed orders in this period"
        description="Try a longer period."
      />
    );
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Totals per country">
      {totals.data.map((total) => (
        <li key={`${total.countryId}-${total.currencyCode}`}>
          <Card className="gap-1 py-3">
            <CardContent className="space-y-2 px-4">
              <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {total.countryName ?? `#${total.countryId}`} · {total.currencyCode}
              </p>
              <dl className="grid grid-cols-2 gap-2">
                <TotalField label="Total">
                  {formatCurrency(total.collected, total.currencyCode)}
                </TotalField>
                <TotalField label="Partner share">
                  {formatCurrency(total.partnerShare, total.currencyCode)}
                </TotalField>
              </dl>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}

function TotalField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-[11px]">{label}</dt>
      <dd className="truncate text-sm font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

function PartnerShareBody({ report }: { report: PartnerShareReport }) {
  const { can } = usePermissions();
  const canSetUpPartner = can('settlements.read') && can('settlements.partners.manage');
  // The Partner's own report has no gross profit, management fee or net profit.
  const showProfit = can('reports.financial.read');
  const period = report.groupBy === 'month' ? 'Month' : 'Day';

  return (
    <div className="space-y-4">
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Net share per country">
        {report.totals.map((total) => (
          <li key={`${total.countryId}-${total.currencyCode}`}>
            <Card className="gap-1 py-3">
              <CardContent className="space-y-1 px-4">
                <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                  {total.countryName ?? `#${total.countryId}`} · {total.currencyCode}
                </p>
                <p className="text-sm">
                  <PartnerName row={total} canSetUp={canSetUpPartner} />
                </p>
                <p className="font-display text-2xl font-bold tabular-nums">
                  {formatCurrency(total.netShare, total.currencyCode)}
                  <span className="text-muted-foreground ms-1.5 text-xs font-normal">
                    net share
                  </span>
                </p>
                <p className="text-muted-foreground text-xs">
                  Partner share {formatCurrency(total.partnerShare, total.currencyCode)} (
                  {pct(total.sharePct)}
                  {showProfit && ' of net profit'}) · given back for{' '}
                  {formatNumber(total.refundsCount)} refunds{' '}
                  {formatCurrency(total.refundAdjustment, total.currencyCode)}
                </p>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>

      {report.unpricedOrders > 0 && (
        <p className="text-status-warning text-xs" role="note">
          {formatNumber(report.unpricedOrders)} orders have no pricing record: no profit or share is
          known for them, so they are not in this report.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Partner share by {period.toLowerCase()}</CardTitle>
          <p className="text-muted-foreground text-xs">
            Refunds are counted on the {period.toLowerCase()} they were paid; the share they give
            back is taken off the net share.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="text-xs" aria-label="Partner share">
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead className="text-end">Orders</TableHead>
                  <TableHead className="text-end">Collected</TableHead>
                  {showProfit && <TableHead className="text-end">Gross profit</TableHead>}
                  {showProfit && <TableHead className="text-end">Mgmt fee</TableHead>}
                  {showProfit && <TableHead className="text-end">Net profit</TableHead>}
                  <TableHead className="text-end">Share %</TableHead>
                  <TableHead className="text-end">Partner share</TableHead>
                  <TableHead className="text-end">Refunds</TableHead>
                  <TableHead className="text-end">Share given back</TableHead>
                  <TableHead className="text-end">Net share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.rows.map((row) => (
                  <TableRow key={`${row.key}-${row.countryId}-${row.currencyCode}`}>
                    <TableCell className="whitespace-nowrap">
                      {formatPeriodKey(row.key, true)}
                    </TableCell>
                    <TableCell>{row.countryName ?? `#${row.countryId}`}</TableCell>
                    <TableCell>
                      <PartnerName row={row} canSetUp={canSetUpPartner} />
                    </TableCell>
                    <ShareCells row={row} showProfit={showProfit} />
                  </TableRow>
                ))}
              </TableBody>
              {report.totals.length > 0 && (
                <TableFooter>
                  {report.totals.map((total) => (
                    <TableRow key={`${total.countryId}-${total.currencyCode}`}>
                      <TableCell>Total</TableCell>
                      <TableCell>{total.countryName ?? `#${total.countryId}`}</TableCell>
                      <TableCell>{total.partnerName ?? '—'}</TableCell>
                      <ShareCells row={total} showProfit={showProfit} />
                    </TableRow>
                  ))}
                </TableFooter>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** The partner, or a muted "No partner set up" leading to where one is added. */
function PartnerName({ row, canSetUp }: { row: PartnerShareRow; canSetUp: boolean }) {
  if (row.partnerName) return <>{row.partnerName}</>;
  return (
    <span className="text-muted-foreground">
      <span>No partner set up</span>
      {canSetUp && (
        <>
          {' · '}
          <Link to={PARTNERS_LINK} className="text-primary hover:underline" data-print="hide">
            Set up a partner
          </Link>
        </>
      )}
    </span>
  );
}

function ShareCells({ row, showProfit }: { row: PartnerShareRow; showProfit: boolean }) {
  const money = (value: number | null) =>
    value === null ? '—' : formatCurrency(value, row.currencyCode);
  return (
    <>
      <TableCell className="text-end tabular-nums">{formatNumber(row.orders)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.collected)}</TableCell>
      {showProfit && (
        <TableCell className="text-end tabular-nums">{money(row.grossProfit)}</TableCell>
      )}
      {showProfit && <TableCell className="text-end tabular-nums">{money(row.mgmtFee)}</TableCell>}
      {showProfit && (
        <TableCell className="text-end tabular-nums">{money(row.netProfit)}</TableCell>
      )}
      <TableCell className="text-end tabular-nums">{pct(row.sharePct)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.partnerShare)}</TableCell>
      <TableCell className="text-end tabular-nums">{formatNumber(row.refundsCount)}</TableCell>
      <TableCell className="text-end tabular-nums">{money(row.refundAdjustment)}</TableCell>
      <TableCell className="text-end font-medium tabular-nums">{money(row.netShare)}</TableCell>
    </>
  );
}
